import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Officer, OfficerRole, PortalType } from '../models/officer.model';
import { ApiClientService } from './api-client.service';

const SESSION_KEY = 'jansunwai.employee.session';
const TOKEN_KEY = 'jansunwai.employee.token';

interface ApiSession {
  accountId: number;
  ipId: string;
  role: OfficerRole | string;
  partyId?: string;
  officerId?: string | number;
  userId?: string | number;
  displayName?: string;
  employeeId?: string;
  email?: string;
  phone?: string;
  department?: string;
  district?: string;
  actorType?: string;
  portalType?: PortalType;
  citizenPortalAvailable?: boolean;
  permissionCodes?: string[];
  fullAccess?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiClientService);

  /** Stored claims are only a hint until GET /auth/session confirms them. */
  private readonly session = signal<Officer | null>(this.readSession());
  private readonly accessToken = signal<string | null>(this.readToken());
  private readonly sessionVerified = signal(false);
  private resolveSessionReady: () => void = () => undefined;
  private readonly sessionReady = new Promise<void>((resolve) => {
    this.resolveSessionReady = resolve;
  });

  readonly officer = computed(() => this.session());
  /** True only after the stored token is confirmed (or there was nothing to restore). */
  readonly isAuthenticated = computed(
    () => this.sessionVerified() && this.session() !== null && !!this.accessToken(),
  );
  readonly token = computed(() => this.accessToken());

  constructor() {
    // Defer the probe so construction finishes before the interceptor injects this service.
    queueMicrotask(() => void this.restoreSession());
  }

  /** Resolves once leftover storage has been confirmed or cleared. */
  whenSessionReady(): Promise<void> {
    return this.sessionReady;
  }

  /** Admin portal login (Admin + desk profiles created by Admin). */
  async loginAdmin(
    username: string,
    password: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ token: string; session: ApiSession }>('/auth/admin/login', {
          username,
          password,
        }),
      );
      if (data.session.role === 'citizen') {
        return { ok: false, message: 'Use Citizen login for citizen accounts.' };
      }
      const officer = this.mapSession(data.session);
      this.applySession(officer, data.token);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /** Compat — same as loginAdmin. */
  async login(username: string, password: string) {
    return this.loginAdmin(username, password);
  }

  /**
   * Request OTP for DM / Records Officer (email or mobile).
   * Other roles receive a 403 from the API.
   */
  async requestOfficerOtp(
    otpChannel: 'email' | 'mobile' = 'email',
    destination: string,
  ): Promise<
    | {
        ok: true;
        challengeId: string;
        destinationMasked: string;
        otpChannel: string;
        expiresInSeconds: number;
        resendAvailableInSeconds: number;
      }
    | { ok: false; message: string }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          challengeId: string;
          destinationMasked: string;
          otpChannel: string;
          otpSentTo: string;
          expiresInSeconds: number;
          resendAvailableInSeconds?: number;
        }>('/auth/officer/otp/request', { otpChannel, destination }),
      );
      return {
        ok: true,
        challengeId: data.challengeId,
        destinationMasked: data.destinationMasked,
        otpChannel: data.otpChannel || data.otpSentTo,
        expiresInSeconds: data.expiresInSeconds,
        resendAvailableInSeconds: data.resendAvailableInSeconds ?? 60,
      };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  async resendOfficerOtp(
    challengeId: string,
  ): Promise<
    | {
        ok: true;
        challengeId: string;
        destinationMasked: string;
        expiresInSeconds: number;
        resendAvailableInSeconds: number;
      }
    | { ok: false; message: string }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          challengeId: string;
          destinationMasked: string;
          expiresInSeconds: number;
          resendAvailableInSeconds?: number;
        }>('/auth/officer/otp/resend', { challengeId }),
      );
      return {
        ok: true,
        challengeId: data.challengeId,
        destinationMasked: data.destinationMasked,
        expiresInSeconds: data.expiresInSeconds,
        resendAvailableInSeconds: data.resendAvailableInSeconds ?? 60,
      };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  async verifyOfficerOtp(
    challengeId: string,
    otp: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ token: string; session: ApiSession }>('/auth/officer/otp/verify', {
          challengeId,
          otp,
        }),
      );
      if (data.session.role === 'citizen') {
        return { ok: false, message: 'Use Citizen login for citizen accounts.' };
      }
      this.applySession(this.mapSession(data.session), data.token);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /**
   * Citizen OTP login. Citizen portal is not built — does not enter Employee Portal.
   */
  async loginCitizen(
    mobile: string,
    otp: string,
    challengeId: string,
  ): Promise<
    | { ok: true; citizenPortalAvailable: false; message: string }
    | { ok: false; message: string }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          token: string;
          session: ApiSession;
          citizenPortalAvailable?: boolean;
          message?: string;
        }>('/auth/citizen/otp/verify', { challengeId, otp, mobile }),
      );
      return {
        ok: true,
        citizenPortalAvailable: false,
        message: data.message || 'Citizen authenticated.',
      };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  async sendCitizenOtp(mobile: string): Promise<{ ok: true; challengeId: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ challengeId: string }>('/auth/citizen/otp/send', { mobile }),
      );
      return { ok: true, challengeId: data.challengeId };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /** Drop local session without calling logout API (e.g. after 401). */
  clearLocalSession(): void {
    this.session.set(null);
    this.accessToken.set(null);
    this.sessionVerified.set(false);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }

  async logout(): Promise<void> {
    // Subscribe first so the interceptor still sees the token and the server can
    // revoke it, but drop the local session without waiting: a slow or hanging
    // revocation must not leave the UI rendering a signed-in shell.
    const revoked = this.accessToken()
      ? firstValueFrom(this.api.post('/auth/admin/logout', {})).catch(() => undefined)
      : Promise.resolve(undefined);
    this.clearLocalSession();
    await revoked;
  }

  /**
   * Persists name/email/phone to the DB via PATCH /auth/profile and applies the
   * server's returned session+token locally. A local-only signal/localStorage
   * patch (the old behavior here) looks like it worked but reverts the moment
   * anything re-reads the real session from the server (e.g. a fresh login, or
   * the token reissue that changePassword already does).
   */
  async updateProfile(
    patch: Partial<Pick<Officer, 'name' | 'phone' | 'email'>>,
  ): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.patch<{ ok: boolean; message: string; token: string; session: ApiSession }>(
          '/auth/profile',
          { name: patch.name, email: patch.email, phone: patch.phone },
        ),
      );
      if (data.token && data.session) {
        this.applySession(this.mapSession(data.session), data.token);
      } else {
        if (data.token) {
          this.accessToken.set(data.token);
          localStorage.setItem(TOKEN_KEY, data.token);
        }
        if (data.session) {
          const officer = this.mapSession(data.session);
          this.session.set(officer);
          localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
        }
        this.sessionVerified.set(true);
      }
      return { ok: true, message: data.message || 'Profile updated.' };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /**
   * Change password via JWT-authenticated API.
   * Backend verifies current password, stores bcrypt hash in MariaDB, returns a fresh JWT.
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ ok: boolean; message: string; token: string; session: ApiSession }>(
          '/auth/change-password',
          { currentPassword, newPassword },
        ),
      );
      if (data.token && data.session) {
        this.applySession(this.mapSession(data.session), data.token);
      } else {
        if (data.token) {
          this.accessToken.set(data.token);
          localStorage.setItem(TOKEN_KEY, data.token);
        }
        if (data.session) {
          const officer = this.mapSession(data.session);
          this.session.set(officer);
          localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
        }
        this.sessionVerified.set(true);
      }
      return { ok: true, message: data.message || 'Password changed successfully.' };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  hasRole(...roles: OfficerRole[]): boolean {
    const current = this.session();
    return !!current && roles.includes(current.role);
  }

  private applySession(officer: Officer, token: string): void {
    this.session.set(officer);
    this.accessToken.set(token);
    this.sessionVerified.set(true);
    localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Leftover localStorage must not count as signed-in until the API agrees.
   * Otherwise the public landing CTA flashes "Go to dashboard" on first open
   * after a server restart or a dead token, then snaps back to Login.
   */
  private async restoreSession(): Promise<void> {
    try {
      const token = this.accessToken();
      const stored = this.session();
      if (!token || !stored) {
        this.clearLocalSession();
        return;
      }
      const data = await firstValueFrom(this.api.get<{ session: ApiSession }>('/auth/session'));
      this.applySession(this.mapSession(data.session), token);
    } catch {
      this.clearLocalSession();
    } finally {
      this.resolveSessionReady();
    }
  }

  private mapSession(s: ApiSession & { designation?: string }): Officer {
    return {
      id: s.userId ?? s.officerId ?? s.partyId ?? s.ipId ?? 'user',
      employeeId: String(s.employeeId || s.officerId || s.userId || 'EMP'),
      name: s.displayName || 'User',
      email: s.email || '',
      phone: s.phone || '',
      role: (s.role as OfficerRole) || 'officer',
      department: s.department || 'Unassigned',
      district: s.district || 'Lucknow',
      portalType: s.portalType || 'admin',
      designation: s.designation,
      permissionCodes: Array.isArray(s.permissionCodes) ? [...s.permissionCodes] : [],
      fullAccess: s.role === 'admin' || s.fullAccess === true,
    };
  }

  private errMessage(err: unknown): string {
    let message = 'Login failed. Is the API running at http://localhost:3040?';
    if (err instanceof HttpErrorResponse) {
      message = err.error?.message || err.message || message;
    } else if (err instanceof Error) {
      message = err.message;
    }
    return message;
  }

  private readSession(): Officer | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as Officer;
    } catch {
      return null;
    }
  }

  private readToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }
}
